import React, { useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  loadState,
  persistState,
  pushRemoteState,
  ensureControls,
  addEquipment as addEq,
  removeEquipment as removeEq,
  setEquipmentStage,
  attachSensor as attachS,
  detachSensor as detachS,
  updateStandalone as updateSA,
  updateDamatexSubSensor as updateDSS,
  getEligibleSensors,
} from "../state/roomState";

import Sidebar from "./Sidebar";
import TopNav from "./TopNav";
import Toast from "./Toast";
import ClimateZone from "./ClimateZone";
import EnvironmentZone from "./EnvironmentZone";
import IrrigationZone from "./IrrigationZone";
import CalibrationForm from "./CalibrationForm";

export default function ControlsPage() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const [rooms, setRooms] = useState(() => loadState());
  const [toastMsg, setToastMsg] = useState("");
  const [toastKey, setToastKey] = useState(0);
  const [showCalibration, setShowCalibration] = useState(false);

  const room = rooms.find((r) => r.id === decodeURIComponent(roomId)) || null;

  const showToast = useCallback((msg) => {
    setToastMsg(msg);
    setToastKey((k) => k + 1);
  }, []);

  const persist = useCallback(() => {
    persistState(rooms);
    pushRemoteState(rooms);
    setRooms([...rooms]);
  }, [rooms]);

  if (!room) {
    return (
      <div style={{ display: "flex", height: "100vh" }}>
        <Sidebar />
        <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <TopNav />
          <div style={{ padding: 40, textAlign: "center" }}>
            <h2>Room not found</h2>
            <button className="btn btn-secondary" style={{ marginTop: 16 }} onClick={() => navigate("/")}>Back to Dashboard</button>
          </div>
        </div>
      </div>
    );
  }

  const controls = ensureControls(room);

  const actions = {
    addEquipment: (type) => { addEq(room, type); persist(); showToast(`Added ${type}`); },
    removeEquipment: (id) => { const eq = controls.equipment.find(e => e.id === id); removeEq(room, id); persist(); showToast(`Removed ${eq?.label || "unit"}`); },
    setStage: (id, stage) => { setEquipmentStage(room, id, stage); persist(); },
    attachSensor: (eqId, sType) => { attachS(room, eqId, sType); persist(); showToast("Sensor attached"); },
    detachSensor: (eqId, sType) => { detachS(room, eqId, sType); persist(); showToast("Sensor detached"); },
    updateStandalone: (key, value) => { updateSA(room, key, value); persist(); },
    updateDamatexSubSensor: (key, value) => { updateDSS(room, key, value); persist(); },
    getEligible: (eqId) => getEligibleSensors(room, eqId),
  };

  return (
    <div style={{ display: "flex", height: "100vh", width: "100vw", overflow: "hidden" }}>
      <Sidebar />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", backgroundColor: "#f8fafc" }}>
        <TopNav />
        <div className="controls-page" style={{ flex: 1, overflowY: "auto" }}>
          <div className="controls-page-header">
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <button className="btn btn-ghost" onClick={() => navigate("/")}>← Back</button>
              <div>
                <h1>{room.label || room.id} — Equipment & Sensors</h1>
                <p style={{ margin: 0, color: "#6b7280", fontSize: 13 }}>Configure installed equipment, stages, and sensor attachments</p>
              </div>
              <select
                value={room.id}
                onChange={(e) => navigate(`/controls/${encodeURIComponent(e.target.value)}`)}
                style={{ marginLeft: "auto", padding: "6px 10px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 14 }}
              >
                {rooms.map((r) => (
                  <option key={r.id} value={r.id}>{r.label || r.id}</option>
                ))}
              </select>
            </div>
          </div>

          {showCalibration ? (
            <div style={{ marginBottom: 24 }}>
              <button
                className="btn btn-ghost"
                style={{ marginBottom: 12 }}
                onClick={() => setShowCalibration(false)}
              >
                ← Back to Equipment
              </button>
              <CalibrationForm standalone={false} roomId={room.id} cage={controls.standalones.damatexCage} />
            </div>
          ) : (
            <>
              <ClimateZone controls={controls} actions={actions} />
              <EnvironmentZone controls={controls} actions={actions} onOpenCalibration={() => setShowCalibration(true)} />
              <IrrigationZone controls={controls} actions={actions} />
            </>
          )}
        </div>
      </div>
      <Toast key={toastKey} message={toastMsg} />
    </div>
  );
}
